import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { useTranslation } from "react-i18next";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDButton from "components/MDButton";

// Custom Components

// API & Models
import {
  getEmployeesByJobId,
  deleteEmployee,
  addEmployee,
  updateEmployee,
  getEmployees, // To fetch all global employees
} from "services/api";
// Assuming you have these APIs exposed:

import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import AddEmployeeDialog from "components/AddEmployeeDialog";
import { getJobs } from "services/api";
import { getBranches } from "services/api";
import { getDepartments } from "services/api";
import EmployeeModel from "models/employeeModel";
import { Avatar } from "@mui/material";
import { PersonOutlined } from "@mui/icons-material";

function EmployeeListDialog({ open, onClose, job, isScenario, companyId, companies, refreshFtes }) {
  // --- 1. Local State for the List View ---
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const { t } = useTranslation();

  // --- 2. State for Reference Data (Lazy Loaded) ---
  const [refData, setRefData] = useState({
    jobs: [],
    branches: [],
    departments: [],
    allEmps: [], // Global list for validation/managers
  });
  const [refDataLoaded, setRefDataLoaded] = useState(false);
  const [loadingRefData, setLoadingRefData] = useState(false);

  // --- 3. Dialog & Action State ---
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null); // For Edit

  const [confOpen, setConfOpen] = useState(false);
  const [empToDelete, setEmpToDelete] = useState(null);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const snackbarRef = useRef();

  // --- 4. Fetch Employees for CURRENT JOB (On Open) ---
  useEffect(() => {
    if (open && job?.jobId && !isScenario) {
      setLoading(true);
      setError(false);

      const fetchData = async () => {
        try {
          const data = await getEmployeesByJobId(job.jobId);
          setEmployees(Array.isArray(data) ? data : []);
        } catch (err) {
          console.error("Failed to fetch employees for job", job.jobId, err);
          setError(true);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [open, job, isScenario]);

  const handleClose = (e) => {
    if (e) e.stopPropagation();
    onClose();
  };

  // --- 5. Lazy Fetch Reference Data (Triggered on Add/Edit Click) ---
  const prepareReferenceData = async () => {
    // If we already loaded data, don't fetch again
    if (refDataLoaded) return true;

    setLoadingRefData(true);
    try {
      // Fetch all required data in parallel
      const [jobsRes, branchesRes, deptsRes, empsRes] = await Promise.all([
        getJobs(),
        getBranches(),
        getDepartments(),
        getEmployees(),
      ]);

      setRefData({
        jobs: Array.isArray(jobsRes) ? jobsRes : [],
        branches: Array.isArray(branchesRes) ? branchesRes : [],
        departments: Array.isArray(deptsRes) ? deptsRes : [],
        allEmps: Array.isArray(empsRes) ? empsRes : [],
      });

      setRefDataLoaded(true); // Mark as loaded so next time it's instant
      return true;
    } catch (err) {
      console.error("Error loading reference data", err);
      snackbarRef.current?.showSnackbar(t("admin.employees.empList.errorLoadForm"), "error");
      return false;
    } finally {
      setLoadingRefData(false);
    }
  };

  // --- 6. Handle Opening Add/Edit ---
  const handleOpenAdd = async (e) => {
    e.stopPropagation();
    const success = await prepareReferenceData();
    if (success) {
      setSelectedEmployee(null); // Clear selection
      setOpenAddDialog(true);
    }
  };

  const handleOpenEdit = async (emp, e) => {
    e.stopPropagation();
    const success = await prepareReferenceData();
    if (success) {
      setSelectedEmployee(emp);
      setOpenAddDialog(true);
    }
  };

  // --- 7. Save Logic (Integrated) ---
  const handleSave = async (newEmployee, departmentId) => {
    const normalizedEmployeeName = newEmployee.name.trim().toLowerCase();

    // Use loaded reference data for duplicate check
    const nameExists = refData.allEmps.some(
      (e) =>
        e.name.trim().toLowerCase() === normalizedEmployeeName &&
        (!selectedEmployee || e.id !== selectedEmployee.id)
    );

    if (nameExists) {
      snackbarRef.current?.showSnackbar(t("admin.employees.empList.employeeNameExists"), "error");
      return;
    }

    const currentCompanyId =
      companyId || (companies && companies.length > 0 ? companies[0].companyId : 1);

    if (selectedEmployee) {
      // === UPDATE ===
      const updatedEmp = { ...selectedEmployee };
      // Map fields...
      updatedEmp.name = newEmployee.name;
      updatedEmp.email = newEmployee.email;
      updatedEmp.phoneNumber = newEmployee.phoneNumber;
      updatedEmp.isAdmin = newEmployee.isAdmin;
      updatedEmp.jobId = newEmployee.job;
      updatedEmp.gender = newEmployee.gender;
      updatedEmp.dob = newEmployee.dob;
      updatedEmp.directManagerId = newEmployee.directManager;
      updatedEmp.branchId = newEmployee.branch;
      updatedEmp.departmentId = departmentId;
      updatedEmp.referencedEmployeeId = newEmployee.referencedEmployeeId;
      updatedEmp.indirectManagerId = newEmployee.indirectManager;
      updatedEmp.jobGroupId = newEmployee.jobGroup;
      updatedEmp.employmentType = newEmployee.employmentType;
      // ... map other fields based on your AddEmployeeDialog output

      const res = await updateEmployee(updatedEmp);
      if (res !== "error") {
        // Update Local List
        setEmployees((prev) => prev.map((e) => (e.id === updatedEmp.id ? updatedEmp : e)));

        // Update Global Ref List (to keep cache fresh)
        setRefData((prev) => ({
          ...prev,
          allEmps: prev.allEmps.map((e) => (e.id === updatedEmp.id ? updatedEmp : e)),
        }));

        snackbarRef.current?.showSnackbar(
          t("admin.employees.empList.employeeUpdateSuccess"),
          "success"
        );
        refreshFtes();
        setOpenAddDialog(false);
      } else {
        snackbarRef.current?.showSnackbar(
          t("admin.employees.empList.errorUpdateEmployee"),
          "error"
        );
      }
    } else {
      // === CREATE ===
      const newEmpModel = new EmployeeModel({
        name: newEmployee.name,
        email: newEmployee.email,
        phoneNumber: newEmployee.phoneNumber,
        role: 2,
        isAdmin: newEmployee.isAdmin,
        jobId: newEmployee.job || job.jobId, // Use form selection or current job context
        gender: newEmployee.gender,
        dob: newEmployee.dob,
        directManagerId: newEmployee.directManager,
        branchId: newEmployee.branch,
        departmentId: departmentId,
        referencedEmployeeId: newEmployee.referencedEmployeeId,
        indirectManagerId: newEmployee.indirectManager,
        companyId: currentCompanyId,
        jobGroupId: newEmployee.jobGroup,
        employmentType: newEmployee.employmentType,
        language: "en",
      });

      const res = await addEmployee(newEmpModel);
      if (res !== "error") {
        const createdEmp = res;

        // Update Local List (Only if the new employee belongs to THIS job)
        // If user changed job in dropdown, we shouldn't show them here, but we usually default to current job
        if (String(createdEmp.jobId) === String(job.jobId)) {
          setEmployees((prev) => [...prev, createdEmp]);
        }

        // Update Global Ref List
        setRefData((prev) => ({
          ...prev,
          allEmps: [...prev.allEmps, createdEmp],
        }));

        snackbarRef.current?.showSnackbar(
          t("admin.employees.empList.employeeAddSuccess"),
          "success"
        );
        refreshFtes();
        setOpenAddDialog(false);
      } else {
        snackbarRef.current?.showSnackbar(t("admin.employees.empList.errorAddEmployee"), "error");
      }
    }
  };

  // --- 8. Delete Logic ---
  const handleDelete = async () => {
    if (!empToDelete) return;
    setLoadingDelete(true);
    try {
      await deleteEmployee(empToDelete);
      setEmployees((prev) => prev.filter((e) => e.id !== empToDelete));

      // Also remove from ref cache
      setRefData((prev) => ({
        ...prev,
        allEmps: prev.allEmps.filter((e) => e.id !== empToDelete),
      }));

      snackbarRef.current?.showSnackbar(
        t("admin.employees.empList.employeeDeleteSuccess"),
        "success"
      );
      setConfOpen(false);
    } catch (err) {
      console.error(err);
      snackbarRef.current?.showSnackbar(t("admin.employees.empList.errorDeleteEmployee"), "error");
    } finally {
      setLoadingDelete(false);
      refreshFtes();

      setEmpToDelete(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      onClick={(e) => e.stopPropagation()}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={2.5}
        pb={1.5}
        bgColor="primary"
        variant="gradient"
      >
        <MDBox>
          <MDTypography variant="h6" fontWeight="bold" color="white" textTransform="capitalize">
            {job?.label || job?.jobName || "Job Position"}
          </MDTypography>
          <MDTypography variant="caption" color="white" opacity={0.8}>
            {isScenario
              ? t("admin.employees.empList.scenarioMode")
              : loading
                ? "Loading..."
                : `${t("admin.employees.employeeDialog.fields.assigned")}: ${employees.length}`}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" gap={1}>
          {/* Add Button with Loading State */}
          {!isScenario && (
            <IconButton
              onClick={handleOpenAdd}
              size="small"
              disabled={loadingRefData}
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.2)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              {loadingRefData ? <CircularProgress size={16} color="inherit" /> : <Icon>add</Icon>}
            </IconButton>
          )}
        </MDBox>
      </MDBox>

      {/* Content */}
      <DialogContent sx={{ maxHeight: "450px", minHeight: "200px", overflowY: "auto" }}>
        {isScenario ? (
          <MDBox textAlign="center" py={6} opacity={0.6}>
            {/* ... Scenario UI ... */}
            <MDTypography variant="button">
              {t("admin.employees.empList.scenarioNoData")}
            </MDTypography>
          </MDBox>
        ) : loading ? (
          <MDBox display="flex" justifyContent="center" alignItems="center" height="200px">
            <CircularProgress color="info" />
          </MDBox>
        ) : error ? (
          <MDBox textAlign="center" py={6} color="error">
            <MDTypography variant="button">
              {t("admin.employees.empList.errorLoadData")}
            </MDTypography>
          </MDBox>
        ) : employees.length === 0 ? (
          <MDBox textAlign="center" py={6} opacity={0.6}>
            <Icon fontSize="large" color="disabled">
              person_off
            </Icon>
            <MDTypography variant="button" display="block" color="text" mt={1}>
              {t("admin.employees.empList.employeeNoAssigned")}
            </MDTypography>
            <MDButton
              variant="text"
              color="info"
              onClick={handleOpenAdd}
              sx={{ mt: 1 }}
              disabled={loadingRefData}
            >
              {loadingRefData
                ? t("admin.employees.empList.loadingForm")
                : t("admin.employees.empList.addFirstEmployee")}
            </MDButton>
          </MDBox>
        ) : (
          <List sx={{ p: 0 }}>
            {employees.map((emp, index) => (
              <React.Fragment key={emp.id || index}>
                <ListItem
                  alignItems="center"
                  sx={{
                    py: 1.5,
                    px: 2.5,
                    "&:hover": { backgroundColor: "#f8f9fa" },
                    transition: "background-color 0.2s",
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={emp.photo ? `data:image/jpeg;base64,${emp.photo}` : undefined}
                      alt={emp.name}
                      sx={{
                        width: 44,
                        height: 44,
                        bgcolor: emp.photo ? "transparent" : "grey.200",
                        color: "grey.600",
                        border: "1px solid",
                        borderColor: "grey.300",
                      }}
                    >
                      {!emp.photo && <PersonOutlined fontSize="small" />}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <MDTypography variant="button" fontWeight="bold" color="dark" display="block">
                        {emp.name}
                      </MDTypography>
                    }
                    secondary={
                      <MDBox display="flex" flexDirection="column">
                        {emp.email && (
                          <MDTypography
                            variant="caption"
                            color="info"
                            sx={{ textDecoration: "none" }}
                          >
                            {emp.email}
                          </MDTypography>
                        )}
                      </MDBox>
                    }
                  />

                  <ListItemSecondaryAction>
                    <MDBox display="flex">
                      {/* Edit Button */}
                      <IconButton
                        edge="end"
                        onClick={(e) => handleOpenEdit(emp, e)}
                        sx={{ color: "info.main", mr: 1 }}
                        disabled={loadingRefData}
                      >
                        {loadingRefData && selectedEmployee?.id === emp.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <Icon>edit</Icon>
                        )}
                      </IconButton>
                      {/* Delete Button */}
                      <IconButton
                        edge="end"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEmpToDelete(emp.id);
                          setConfOpen(true);
                        }}
                        sx={{ color: "error.main" }}
                      >
                        <Icon>delete_outline</Icon>
                      </IconButton>
                    </MDBox>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < employees.length - 1 && <Divider component="li" sx={{ my: 0 }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>

      {/* --- Add/Edit Dialog (Only renders if we have data) --- */}
      {openAddDialog && (
        <AddEmployeeDialog
          open={openAddDialog}
          onClose={() => {
            setOpenAddDialog(false);
            setSelectedEmployee(null);
          }}
          // Pass Lazy Loaded Data
          jobs={refData.jobs}
          branches={refData.branches}
          departments={refData.departments}
          employees={refData.allEmps}
          onSave={handleSave}
          employee={selectedEmployee} // Null for Add, Object for Edit
          // To pre-fill the job dropdown
          defaultJobId={selectedEmployee ? selectedEmployee.jobId : job?.jobId}
        />
      )}

      {/* --- Delete Confirmation --- */}
      <ConfirmationDialog
        open={confOpen}
        onClose={() => setConfOpen(false)}
        message="Are you sure you want to delete this employee?"
        onConfirm={handleDelete}
        loading={loadingDelete}
      />

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Dialog>
  );
}

EmployeeListDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  job: PropTypes.object,
  isScenario: PropTypes.bool,
  companyId: PropTypes.number,
  companies: PropTypes.array,
  refreshFtes: PropTypes.func,
};

export default EmployeeListDialog;
